import React, { Suspense } from "react";
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormHandles } from "models/propTypes/genericFormProps";
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
//#endregion
//#region components
import {
  PageLayouts,
  EntityTitle,
  ViewPanel,
  FileViewerContainer,
  Skeletons,
  GenericCommentsView,
} from "components/common";
import ReceiptDetailsFormContainer from "./forms/detailsFormContainer";
import ReceiptFooter from "./receiptFooter";
import ReceiptSimilarList from "./receiptSimilarList";
import ReceiptStateFlow from "./stateFlow";
import ReceiptVouchers from "./receiptVouchers";
import useDetailsFormHelper from "./forms/helpers/useDetailsFormHelper";
import useReceiptDownload from "./useDownload";
import { useReceiptService } from "serviceHooks";
import {
  CostForwardingMarkingProvider,
  isForwardableReceipt,
  useDocumentPageCostForwarding,
} from "components/costForwardingMarking";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

interface ReceiptViewInnerProps {
  receipt?: WintSuperkollAPIModel.Receipt;
  reloadKey: number;
  loadReceipt: (
    receiptId: number,
    onSuccess?: (receipt: WintSuperkollAPIModel.Receipt) => void,
    onError?: () => void
  ) => void;
}

const ReceiptViewInner: React.FC<ReceiptViewInnerProps> = (props) => {
  const receiptService = useReceiptService();
  const { receipt, reloadKey: key, loadReceipt } = props;
  const formRef = React.useRef<FormHandles>(null);

  const attachments: ImageMeta[] =
    (receipt &&
      receipt.attachments?.map((meta) => ({
        id: meta.id,
        contentType: meta.contentType,
        numberOfPages: 1,
        data: meta.data,
        loading: (meta as unknown as ImageMeta).loading,
      }))) ??
    [];

  const { receiptDetailsValidationScheme } = useDetailsFormHelper();

  const costForwarding = useDocumentPageCostForwarding(
    "receipt",
    receipt?.id,
    receipt?.supplierName,
    isForwardableReceipt(receipt),
    receipt?.state
  );

  const onReceiptSubmit = (submitFunc?: (values: any) => Promise<any>, isSoftValidation?: boolean) => {
    return (
      (formRef.current &&
        formRef.current.submit(
          costForwarding.withoutStaleMarking(submitFunc),
          !isSoftValidation ? receiptDetailsValidationScheme(false) : undefined
        )) ||
      new Promise<void>((resolve) => resolve())
    );
  };

  const reloadReceipt = (onSuccess?: (receipt: WintSuperkollAPIModel.Receipt) => void, onError?: () => void) => {
    if (receipt) {
      loadReceipt(receipt.id, onSuccess, onError);
    }
  };

  const markAsDoneReceipt = () => {
    receipt && loadReceipt(receipt.id);
  };

  const onDownloadClick = useReceiptDownload(receipt?.id ?? 0);

  const [displayType, setDisplayType] = React.useState<StyleEnums.DisplayType>(StyleEnums.DisplayType.Info);

  React.useEffect(() => {
    let currentDisplayType = StyleEnums.DisplayType.Info;
    if (
      receipt?.state === WintSuperkollAPIModel.ReceiptStateApiModel.Booked &&
      receipt?.paymentState === WintSuperkollAPIModel.PaymentState.Paid
    ) {
      currentDisplayType = StyleEnums.DisplayType.Success;
    } else if (receipt?.state === WintSuperkollAPIModel.ReceiptStateApiModel.Canceled)
      currentDisplayType = StyleEnums.DisplayType.None;

    if (displayType !== currentDisplayType) setDisplayType(currentDisplayType);
  }, [receipt?.state, receipt?.paymentState]);

  const upperLeftComponent = React.useMemo(
    () => (
      <EntityTitle
        status={
          receipt?.state !== undefined ? receiptService.getStateText(receiptService.apiToViewStatus(receipt.state)) : ""
        }
        subTitle={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.ReceiptFrom)}
        title={receipt?.createdByPersonName ?? ""}
        loading={receipt === undefined}
      />
    ),
    [receipt]
  );

  const upperRightComponent = React.useMemo(
    () =>
      receipt ? (
        <ReceiptStateFlow key={`stateFlow-${receipt.state}`} id={receipt.id} />
      ) : (
        <Skeletons.StateFlowSkeleton key="steps-skeleton" stepCount={4} />
      ),
    [receipt]
  );

  const fileViewerComponent = React.useMemo(
    () => (
      <FileViewerContainer
        key={receipt?.id}
        meta={attachments}
        parentId={receipt?.id ?? 0}
        onDownloadClick={onDownloadClick}
        loading={receipt === undefined}
        getFile={receipt ? receiptService.getAttachmentData.bind(null, receipt.id) : undefined}
      />
    ),
    [receipt?.id, receipt?.attachments, displayType]
  );

  return (
    <PageLayouts.EntityViewLayout
      displayType={displayType}
      upperLeftComponent={upperLeftComponent}
      upperRightComponent={upperRightComponent}
      lowerLeftComponent={fileViewerComponent}
      footer={
        receipt ? (
          <ReceiptFooter
            receipt={receipt}
            receiptFormSubmitHandler={onReceiptSubmit}
            reloadReceipt={reloadReceipt}
            markAsDone={markAsDoneReceipt}
            costForwardingAction={costForwarding.menuAction}
          />
        ) : (
          <></>
        )
      }
    >
      <>
        <CostForwardingMarkingProvider value={costForwarding.context}>
          <ViewPanel
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Details)}
            icon={IconDefinitionConstants.WEdit}
            classes={{ content: cssExports.detailsFormContainer }}
            children={
              receipt && key > 0 ? (
                <Suspense fallback={<Skeletons.FormSkeleton />}>
                  <ReceiptDetailsFormContainer
                    key={key}
                    ref={formRef}
                    receipt={receipt}
                    fileViewer={fileViewerComponent}
                  />
                </Suspense>
              ) : (
                <Skeletons.FormSkeleton />
              )
            }
          />
        </CostForwardingMarkingProvider>

        {receipt ? (
          <>
            <ReceiptVouchers key={`vouchers-${receipt.state}`} id={receipt.id} />
            <GenericCommentsView
              key={`comments-${receipt?.state}`}
              parentId={receipt?.id}
              type={WintSuperkollAPIModel.GenericCommentType.Receipt}
            />
            <ReceiptSimilarList key={`similarList-${receipt.amount}-${receipt.currency}`} id={receipt.id} />
          </>
        ) : (
          <></>
        )}
      </>
    </PageLayouts.EntityViewLayout>
  );
};

export default ReceiptViewInner;
